import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { useStoreContext } from 'features/store';

import { ServiceMode } from 'generated/graphql-gateway';
import { selectors, useAppSelector } from 'state/global-state';
import { useOrderContext } from 'state/order';
import { useServiceModeLive } from 'state/service-mode';

import { isDeliveryQuoteOrAddressStale } from './delivery-reprice-utils';
import { useCheckoutContext } from './use-checkout-context';

export const useCheckoutLoadingState = () => {
  const {
    isSubmittingOrder,
    isBuildingCommitOrderPayload,
    isLoadingPaymentMethods,
    isSavingPaymentMethods,
    isOrderLoading,
    isRepricedOrderLoading,
    isMenuLoading,
    isSelectingStore,
    isFetchingStore,
    serverOrder,
  } = useCheckoutContext();
  const { store } = useStoreContext();
  const {
    checkoutRepriceRequestId,
    checkoutRepriceRequestCompletedId,
    deliveryAddress,
    quoteId,
  } = useOrderContext();
  const serviceMode = useServiceModeLive();
  const { isMinimalCartStoreRepricing } = useMinimalCart();

  // Read the pricing state from Redux (set by price order)
  const isPricingOrderFromCart = useAppSelector(selectors.ordering.selectIsPricingOrder);
  const selectedStoreNumber = store?.number ?? null;
  const serverOrderStoreNumber =
    serverOrder?.cart?.storeDetails?.storeNumber ??
    serverOrder?.cart?.storeId?.toString?.() ??
    null;
  const isStoreChangeRepricePending = Boolean(
    checkoutRepriceRequestId &&
      selectedStoreNumber &&
      serverOrderStoreNumber &&
      selectedStoreNumber !== serverOrderStoreNumber
  );
  const isDeliveryRepriceRequestPending = Boolean(
    checkoutRepriceRequestId > checkoutRepriceRequestCompletedId &&
      serviceMode === ServiceMode.DELIVERY &&
      isDeliveryQuoteOrAddressStale({
        quoteId,
        serverOrderDeliveryQuoteId: serverOrder?.delivery?.quoteId,
        deliveryAddress,
        serverOrderDropoff: serverOrder?.delivery?.dropoff,
      })
  );

  // Combine local order loading state with cart pricing state
  const isOrderPriceLoading =
    isOrderLoading ||
    isRepricedOrderLoading ||
    isPricingOrderFromCart ||
    isMinimalCartStoreRepricing ||
    isStoreChangeRepricePending ||
    isDeliveryRepriceRequestPending;

  const isCheckoutInitializing =
    isOrderPriceLoading ||
    isLoadingPaymentMethods ||
    isMenuLoading ||
    isSelectingStore ||
    isFetchingStore;

  const isPlaceOrderLoading =
    isOrderPriceLoading ||
    isSubmittingOrder ||
    isBuildingCommitOrderPayload ||
    isLoadingPaymentMethods;

  return {
    isOrderPriceLoading,
    isSubmittingOrder,
    isBuildingCommitOrderPayload,
    isLoadingPaymentMethods,
    isSavingPaymentMethods,
    isCheckoutLoading: isCheckoutInitializing || isSubmittingOrder || isBuildingCommitOrderPayload,
    isPlaceOrderLoading,
  };
};
